import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'

import {
  FormattedMessage,
  injectIntl,
} from 'react-intl'

import { updateUser } from '../../../actions/user'
import { updateUserCustomer } from '../../../actions/actions'
import { CapitalButton } from '../Navigation/Buttons'
import AppButton from '../Navigation/AppButton'

import {
  EmailInput,
  SelectInput,
  TextInput,
  isNumber,
  isEmail,
} from '../Inputs/Inputs'

import AppStore from '../../../stores/AppStore'

import styles from '../../../../css/home.module.css'

const ENGLISH_LANGUAGE = 'en'
const SPANISH_LANGUAGE = 'es'

const Groups = function Groups ({ customerID, groups, userID }) {
  if (!groups || !groups.length) {
    return (
      <>
      </>
    )
  }

  const link = `/${customerID}/settings/users/${userID}/groups`
  const groupsList = groups.filter(Boolean).map((group, index) => {
    const { name, icon, deviceGroup } = group
    return (
      <AppButton
        link={link}
        icon={icon}
        label={name || deviceGroup}
        key={`${name || 'group'}|${icon || 'icon'}|${deviceGroup || 'deviceGroup'}|${index}`}
      />
    )
  })

  return (
    <div className={styles.list} style={{ marginTop: '0px' }}>
      {groupsList}
    </div>
  )
}

function Edit ({
  groups = [],
  name,
  firstName,
  lastName,
  email,
  language,
  level,
  user,
  userCustomer,
  intl: { formatMessage },
  userID,
  getUserCustomer,
}) {
  const navigate = useNavigate()

  const [invalid, setInvalid] = useState(true)
  const [changesMade, setChangesMade] = useState(false)
  const [saved, setSaved] = useState(false)
  const [groupsState, setGroupsState] = useState(groups)
  const [nameState, setNameState] = useState(name)
  const [firstNameState, setFirstNameState] = useState(firstName)
  const [lastNameState, setLastNameState] = useState(lastName)
  const [emailState, setEmailState] = useState(email)
  const [languageState, setLanguageState] = useState(language)
  const [levelState, setLevelState] = useState(level)
  const [userState, setUserState] = useState(user)
  const [userCustomerState, setUserCustomerState] = useState(userCustomer)

  useEffect(() => {
    setGroupsState(groups || [])
  }, [groups])

  const editGroups = useCallback(() => {
    const { id: customerID } = AppStore.getCustomer()
    navigate(`/${customerID}/settings/users/${userID}/groups`)
  }, [navigate, userID])

  const childChange = useCallback((event) => {
    handleChange(event.target.name, event.target.value)
  }, [])

  const handleChange = useCallback((field, value, callback) => {
    let newValue = value
    if (value !== '' && isNumber(value)) { newValue = Number(value) }

    // Update the appropriate state based on field
    if (field === 'groups') setGroupsState(newValue)
    if (field === 'name') setNameState(newValue)
    if (field === 'firstName') setFirstNameState(newValue)
    if (field === 'lastName') setLastNameState(newValue)
    if (field === 'email') setEmailState(newValue)
    if (field === 'language') setLanguageState(newValue)
    if (field === 'level') setLevelState(newValue)
    if (field === 'user') setUserState(newValue)
    if (field === 'userCustomer') setUserCustomerState(newValue)

    setChangesMade(true)

    if (callback) callback()
    validateEmail()
  }, [])

  const validateEmail = useCallback((callback) => {
    let newInvalid = true

    if (isEmail(emailState)) {
      newInvalid = false
      if (levelState === 20 || levelState === 10 || levelState === 0) {
        newInvalid = false
      } else {
        newInvalid = true
      }
    } else {
      newInvalid = true
    }

    setInvalid(newInvalid)

    if (saved === 'error') {
      setSaved(false)
    }

    if (callback) callback()
  }, [emailState, levelState, saved])

  const saveButtonFunction = useCallback(() => {
    const { userCustomer: currentUserCustomer } = AppStore.data
    const {
      customer_id,
      user: {
        locale: userLocale,
      },
      user_id: currentUserID,
    } = currentUserCustomer

    const userChanges = {}

    userChanges.first_name = firstNameState
    userChanges.last_name = lastNameState
    userChanges.email = emailState
    userChanges.locale = { ...userLocale }
    userChanges.locale.language = languageState

    updateUser(
      currentUserID,
      userChanges,
      (error) => {
        if (error) {
          setSaved('error')
        } else {
          setSaved(true)
          setTimeout(() => {
            setSaved(false)
            setChangesMade(false)
          }, 500)
          getUserCustomer()
        }
      }
    )

    const {
      permissions: {
        level: userCustomerLevel,
      },
    } = currentUserCustomer

    const userCustomerChanges = {}

    let userCustomerChanged = false

    if (userCustomerLevel !== levelState) {
      userCustomerChanges.level = levelState
      userCustomerChanged = true
    }

    if (userCustomerChanged) {
      updateUserCustomer(
        currentUserID,
        customer_id,
        { permissions: userCustomerChanges },
        (error) => {
          if (error) {
            setSaved('error')
          } else {
            setSaved(true)
            setTimeout(() => {
              setSaved(false)
            }, 500)
          }
        }
      )
    }
  }, [firstNameState, lastNameState, emailState, languageState, levelState, getUserCustomer])

  let saveButtonStyle = `${styles.saveButton} saveButton`

  if (invalid || !changesMade) {
    saveButtonStyle = `${styles.saveButton} ${styles.saveButtonHidden} saveButton`
  } else if (saved === true) {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonSuccess}`
  } else if (saved === 'error') {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonError}`
  }

  // Get level from AppStore instead of context
  const editorLevel = AppStore.getUser().permissions.level || 0

  const {
    groups_enabled: groupsEnabled,
    id: customerID,
  } = AppStore.getCustomer()

  const userLevelOptions = [
    {
      value: 0,
      name: formatMessage({
        id: 'invite.accountType.viewer',
        defaultMessage: 'Viewer',
        description: '',
      }),
    },
    {
      value: 10,
      name: formatMessage({
        id: 'invite.accountType.manager',
        defaultMessage: 'Manager',
        description: '',
      }),
    },
  ]

  if (editorLevel >= 20) {
    userLevelOptions.push({
      value: 20,
      name: formatMessage({
        id: 'invite.accountType.admin',
        defaultMessage: 'Administrator',
        description: '',
      }),
    })
  }

  return (
    <div>
      <div className={`${styles.userSectionEdit} ${styles.userSection}`}>
        <div className={styles.userEditInput}>
          <section className={styles.userInput}>
            <FormattedMessage
              id='invite.firstName'
              defaultMessage='First Name'
              description='First Name label'
              tagName='h5'
            />
            <div className={styles.userInputValueEdit}>
              <TextInput
                value={firstNameState}
                childChange={childChange}
                field='firstName'
              />
            </div>
          </section>
          <section className={styles.userInput}>
            <FormattedMessage
              id='invite.lastName'
              defaultMessage='Last Name'
              description='Last Name label'
              tagName='h5'
            />
            <div className={styles.userInputValueEdit}>
              <TextInput
                value={lastNameState}
                childChange={childChange}
                field='lastName'
              />
            </div>
          </section>
          <section className={styles.userInput}>
            <FormattedMessage
              id='invite.email'
              defaultMessage='Email'
              description='Email label'
              tagName='h5'
            />
            <div className={styles.userInputValueEdit}>
              <EmailInput
                value={emailState}
                childChange={childChange}
                field='email'
                disabled
              />
            </div>
          </section>
          <section>
            <FormattedMessage
              id='profile.language.name'
              defaultMessage='Language'
              description='Language label'
              tagName='h5'
            />
            <div className={styles.userInputValueEdit}>
              <SelectInput
                value={languageState}
                field='language'
                childChange={childChange}
                options={[
                  {
                    value: ENGLISH_LANGUAGE,
                    selected: (languageState === ENGLISH_LANGUAGE),
                    name: formatMessage({
                      id: 'profile.language.english',
                      defaultMessage: 'English',
                      description: 'English language selection',
                    }),
                  },
                  {
                    value: SPANISH_LANGUAGE,
                    selected: (languageState === SPANISH_LANGUAGE),
                    name: formatMessage({
                      id: 'profile.language.spanish',
                      defaultMessage: 'Spanish',
                      description: 'Spanish language selection',
                    }),
                  },
                ]}
                style={styles.selectInput}
              />
            </div>
          </section>
          {editorLevel >= 20
            ? (
              <section className={styles.userInput}>
                <FormattedMessage
                  id='invite.accountType.name'
                  defaultMessage='Account Type'
                  description='Account Type label'
                  tagName='h5'
                />
                <div className={styles.userInputValueEdit}>
                  <SelectInput
                    value={levelState}
                    field='level'
                    childChange={childChange}
                    options={userLevelOptions}
                    style={styles.selectInput}
                  />
                </div>
              </section>
              )
            : ''}
        </div>
      </div>
      <CapitalButton
        text={formatMessage({
          id: 'general.save',
          defaultMessage: 'Save',
          description: '',
        })}
        buttonFunction={saveButtonFunction}
        hSize='h2'
        style={saveButtonStyle}
        disabled={invalid}
      />

      {groupsEnabled
        ? (
          <button
            type='button'
            className={`${styles.userSectionEdit} ${styles.userSection}`}
            style={{ border: 'none' }}
            onClick={() => editGroups()}
          >
            <FormattedMessage
              id='user.edit.group.permissions'
              defaultMessage='Group Permissions'
              description='Group Permissions label'
            />
            <i
              title={formatMessage({
                id: 'user.edit.editgroups',
                defaultMessage: 'edit groups',
                description: 'edit groups help',
              })}
              style={{
                border: 'none',
                background: 'inherit',
                color: 'black',
                float: 'right',
                fontSize: '34px',
              }}
            >
              <i className='fa fa-edit' />
            </i>
          </button>
          )
        : ''}

      {groupsEnabled
        ? (
          <Groups
            customerID={customerID}
            groups={groupsState}
            userID={userID}
          />
          )
        : ''}

    </div>
  )
}

Edit.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  groups: PropTypes.array,
  name: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  language: PropTypes.string,
  level: PropTypes.number,
  user: PropTypes.object,
  userCustomer: PropTypes.object,
  userID: PropTypes.string.isRequired,
  getUserCustomer: PropTypes.func.isRequired,
}

export default injectIntl(Edit)
